import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { XCircle } from "lucide-react";
import { useAppDispatch } from "../../../hooks";
import { updateStore } from "../../../slices/storeSlice";
import { toast } from "react-toastify";

interface UpdateStoreBasicInfoProps {
  store: {
    id: string;
    name: string;
    location?: string;
    contactInfo?: string;
  };
  onClose: () => void;
  isOpen: boolean;
  onComplete: () => void;
}

const EditStoreSchema = Yup.object().shape({
  name: Yup.string().required("Store name is required"),
  location: Yup.string().optional(),
  contactInfo: Yup.string().optional(),
});

const UpdateStoreBasicInfoModal: React.FC<UpdateStoreBasicInfoProps> = ({
  store,
  onClose,
  isOpen,
  onComplete
}) => {

  const dispatch = useAppDispatch();

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await dispatch(updateStore({ id: store.id, data: values })).unwrap();
      onComplete();
      onClose();
    } catch (error) {
      console.error("Failed to update store: ", error);
      toast.error("Failed to update store. Please try again");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Edit Store Information
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XCircle />
          </button>
        </div>

        {/* Form */}
        <Formik
          initialValues={{
            name: store.name || "",
            location: store.location || "",
            contact_info: store.contactInfo || "",
          }}
          validationSchema={EditStoreSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="grid gap-4">
              {/* Store Name */}
              <label className="form-label">
                Store Name
                <Field
                  name="name"
                  className="form-input mt-1 w-full"
                  placeholder="Enter store name"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </label>

              {/* Location & Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="form-label">
                  Location
                  <Field
                    name="location"
                    className="form-input mt-1 w-full"
                    placeholder="Enter store location"
                  />
                  {errors.location && touched.location && (
                    <p className="text-red-500 text-xs mt-1">{errors.location}</p>
                  )}
                </label>

                <label className="form-label">
                  Contact Info
                  <Field
                    name="contactInfo"
                    className="form-input mt-1 w-full"
                    placeholder="Enter phone or email"
                  />
                  {errors.contact_info && touched.contact_info && (
                    <p className="text-red-500 text-xs mt-1">{errors.contact_info}</p>
                  )}
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-white flex items-center gap-2 disabled:opacity-70"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loader"></span>
                      <span>Saving</span>
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default UpdateStoreBasicInfoModal;
